import { useEffect, useState } from "react";
import { Box, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ModelService } from "@/services/ModelService";
import { ScoringConfigPanel, INHERITANCE_STATE, scoringConfigFromApi, scoringConfigToApi } from "@/scoringUi";
import { buildScoringLabels } from "./scoringLabels";

/**
 * Spec 2 §5: criteria the admin defined at the scenario level, shown here so
 * the client can see what's inherited and, when allowed, override it for
 * this one recruitment. Distinct from the recruitment-native sections
 * (Availability, Financial expectations) which have no scenario counterpart
 * -- this section is entirely driven by whatever the resolved scoring model
 * returns, never a hardcoded field list (spec §4's "not hardcoded" requirement).
 *
 * @param {object} props
 * @param {string|null} props.recruitmentId - existing recruitment id, if editing one
 * @param {string|null} props.scenarioId - chosen scenario id (used when recruitmentId isn't saved yet)
 * @param {Array<{criterionKey: string, scoring: object}>} props.scoringOverrides - jobOffer.scoringOverrides (API-shaped)
 * @param {(next: Array) => void} props.onOverridesChange
 * @param {(model: object|null) => void} [props.onModelLoaded] - fires whenever the resolved scoring model (re)loads, so a host like ScoringModelSummary can read scenarioMaxScore without a second fetch
 */
const InheritedCriteriaSection = ({ recruitmentId, scenarioId, scoringOverrides, onOverridesChange, onModelLoaded }) => {
    const { t } = useTranslation("jobOffer");
    const [model, setModel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const labels = buildScoringLabels(t);

    useEffect(() => {
        if (!recruitmentId && !scenarioId) {
            setModel(null);
            onModelLoaded?.(null);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        const fetcher = recruitmentId
            ? ModelService.getRecruitmentScoringModel(recruitmentId)
            : ModelService.getScenarioScoringModel(scenarioId);
        fetcher
            .then((data) => {
                setModel(data);
                onModelLoaded?.(data);
            })
            .catch(() => {
                setModel(null);
                onModelLoaded?.(null);
            })
            .finally(() => setIsLoading(false));
    }, [recruitmentId, scenarioId]);

    if (!recruitmentId && !scenarioId) return null;
    if (isLoading) return null;
    // Local criteria have no overrideMode; only scenario ones are inherited.
    const scenarioCriteria = (model?.criteria ?? []).filter((c) => c.overrideMode);
    if (!scenarioCriteria.length) return null;

    const overridesByKey = Object.fromEntries((scoringOverrides ?? []).map((o) => [o.criterionKey, o]));

    const setOverride = (criterionKey, scoring) => {
        const next = (scoringOverrides ?? []).filter((o) => o.criterionKey !== criterionKey);
        next.push({ criterionKey, scoring: scoringConfigToApi(scoring) });
        onOverridesChange(next);
    };

    return (
        <VStack w="full" align="stretch" gap={3}>
            <Text color="gray.900" fontWeight={700} fontSize="md">{t("step5.scoring.inheritedTitle")}</Text>
            {scenarioCriteria.map((c) => {
                const hasOverride = !!overridesByKey[c.criterionKey];
                const inheritanceState = hasOverride
                    ? INHERITANCE_STATE.OVERRIDDEN
                    : c.overrideMode === "Locked"
                        ? INHERITANCE_STATE.LOCKED
                        : INHERITANCE_STATE.OVERRIDABLE;
                // Editable only once the client has actually made a local copy (OVERRIDDEN);
                // LOCKED and not-yet-customized OVERRIDABLE are both read-only display.
                const readOnly = inheritanceState !== INHERITANCE_STATE.OVERRIDDEN;
                const effectiveScoring = hasOverride
                    ? scoringConfigFromApi(overridesByKey[c.criterionKey].scoring)
                    : scoringConfigFromApi(c.scoring);

                return (
                    <Box key={c.criterionKey}>
                        <Text color="gray.600" fontSize="sm" mb={1}>{c.displayName || c.name}</Text>
                        <ScoringConfigPanel
                            config={effectiveScoring}
                            onChange={(scoring) => setOverride(c.criterionKey, scoring)}
                            readOnly={readOnly}
                            inheritanceState={inheritanceState}
                            onCustomizeForRecruitment={() => setOverride(c.criterionKey, scoringConfigFromApi(c.scoring))}
                            labels={labels}
                        />
                    </Box>
                );
            })}
        </VStack>
    );
};

export default InheritedCriteriaSection;
